// Who's contributing to AI at Brookfield, worked out from AI Initiatives and
// the AI Library: per person (top submitters, your level) and per Champions
// community (the Champions Cup). Everything scores on the same points.
import { workStage } from "../hooks/useBusinessGroups";
import { communityForDepartment, type CommunityId } from "../data/championsDirectory";
import type { InitiativeItem, LibraryItem } from "../types/navigator";

/** Points for each kind of contribution. */
export const POINTS = {
  idea: 2, // an idea submitted to AI Initiatives
  building: 3, // extra when it's being built
  live: 5, // extra when it's live
  tool: 3, // a prompt, agent or app shared in the AI Library
  likes: 1, // per 5 likes on your tools
};

export type ToolKind = "Prompt" | "Agent" | "App" | "Other";
const toolKind = (t: string): ToolKind => (/prompt/i.test(t) ? "Prompt" : /agent|bot/i.test(t) ? "Agent" : /app/i.test(t) ? "App" : "Other");

export type Contributor = {
  key: string;
  name: string;
  email: string;
  ideas: number;
  building: number;
  live: number;
  tools: Record<ToolKind, number>;
  likes: number;
  points: number;
  /** When they last added something. */
  latest: string;
};

/** "Siva, Suhi" → "Suhi Siva". */
export const displayName = (name: string) => {
  const m = name.match(/^\s*([^,]+),\s*(.+)$/);
  return (m ? `${m[2]} ${m[1]}` : name).trim();
};

/** Who to credit: for an initiative, the person named in "Your name" (people
 *  often enter ideas for others), otherwise whoever added the item. People are
 *  matched by name, so the same person's ideas and tools add up. */
const author = (item: InitiativeItem | LibraryItem) => {
  const creator = displayName(String(item.raw._createdByName ?? ""));
  const creatorEmail = String(item.raw._createdByEmail ?? "").toLowerCase();
  const named = item.kind === "initiative" ? displayName(item.submittedBy) : "";
  const name = named || creator;
  const email = !named || named.toLowerCase() === creator.toLowerCase() ? creatorEmail : "";
  return { key: name.toLowerCase(), email, name };
};

/** People left out of the leaderboards (Top contributors and the Champions
 *  Cup), by email or name, e.g. whoever runs the program and adds a lot on
 *  everyone's behalf. Their own level and badges still count. */
export type Exclusions = string[];

const excluded = (a: { name: string; email: string }, out: Exclusions) =>
  out.some((x) => {
    const v = x.trim().toLowerCase();
    return !!v && (v === a.email || v === a.name.toLowerCase());
  });

/** Leaves excluded people out of a ranking. */
export const withoutExcluded = (ranked: Contributor[], out: Exclusions) => ranked.filter((c) => !excluded(c, out));

const counted = (t: LibraryItem) => !/reject|archiv|retire|draft/i.test(t.status);

export type Window = "all" | "quarter";

/** The start of the current calendar quarter, as YYYY-MM-DD. */
export function quarterStart(now = new Date()): string {
  const m = Math.floor(now.getMonth() / 3) * 3;
  return `${now.getFullYear()}-${String(m + 1).padStart(2, "0")}-01`;
}
const within = (created: string | undefined, since: string | null) => !since || (created ?? "").slice(0, 10) >= since;

export function scoreInitiative(i: InitiativeItem): number {
  const stage = workStage(i.status);
  return POINTS.idea + (stage === "building" ? POINTS.building : stage === "live" ? POINTS.building + POINTS.live : 0);
}
export const scoreTool = (t: LibraryItem) => POINTS.tool + Math.floor(t.likes / 5) * POINTS.likes;

/** Everyone who has added ideas or tools, best first. */
export function rankContributors(initiatives: InitiativeItem[], library: LibraryItem[], since: string | null = null): Contributor[] {
  const people = new Map<string, Contributor>();
  const get = (item: InitiativeItem | LibraryItem) => {
    const a = author(item);
    if (!a.key) return null;
    let c = people.get(a.key);
    if (!c) {
      c = { ...a, ideas: 0, building: 0, live: 0, tools: { Prompt: 0, Agent: 0, App: 0, Other: 0 }, likes: 0, points: 0, latest: "" };
      people.set(a.key, c);
    }
    if (!c.email && a.email) c.email = a.email;
    if ((item.created ?? "") > c.latest) c.latest = item.created ?? "";
    return c;
  };
  for (const i of initiatives) {
    if (!within(i.created, since)) continue;
    const c = get(i);
    if (!c) continue;
    const stage = workStage(i.status);
    c.ideas += 1;
    if (stage === "building") c.building += 1;
    if (stage === "live") c.live += 1;
    c.points += scoreInitiative(i);
  }
  for (const t of library) {
    if (!counted(t) || !within(t.created, since)) continue;
    const c = get(t);
    if (!c) continue;
    c.tools[toolKind(t.assetType)] += 1;
    c.likes += t.likes;
    c.points += scoreTool(t);
  }
  return [...people.values()].sort((a, b) => b.points - a.points || b.latest.localeCompare(a.latest));
}

export type CommunityScore = { id: CommunityId; points: number; items: number };

/** Points per Champions community, from the team or department on each item. */
export function scoreCommunities(
  initiatives: InitiativeItem[],
  library: LibraryItem[],
  ids: CommunityId[],
  since: string | null = null,
  out: Exclusions = []
): CommunityScore[] {
  const score = new Map(ids.map((id) => [id, { id, points: 0, items: 0 }]));
  const add = (dept: string, pts: number) => {
    const s = score.get(communityForDepartment(dept) as CommunityId);
    if (s) {
      s.points += pts;
      s.items += 1;
    }
  };
  for (const i of initiatives) if (within(i.created, since) && !excluded(author(i), out)) add(i.team, scoreInitiative(i));
  for (const t of library) if (counted(t) && within(t.created, since) && !excluded(author(t), out)) add(t.department, scoreTool(t));
  return [...score.values()].sort((a, b) => b.points - a.points);
}

/** Levels people move through as they contribute. */
export const LEVELS = [
  { name: "Explorer", from: 0 },
  { name: "Contributor", from: 5 },
  { name: "Builder", from: 15 },
  { name: "Trailblazer", from: 30 },
  { name: "AI Legend", from: 60 },
];

export function levelFor(points: number) {
  const i = LEVELS.reduce((at, l, j) => (points >= l.from ? j : at), 0);
  const next = LEVELS[i + 1];
  return {
    number: i + 1,
    name: LEVELS[i].name,
    next: next ?? null,
    /** 0–1 progress from this level to the next. */
    progress: next ? (points - LEVELS[i].from) / (next.from - LEVELS[i].from) : 1,
  };
}

export type Badge = { id: string; label: string; earned: boolean; hint: string };

/** Achievements for one person's contributions. */
export function badgesFor(c: Contributor | undefined): Badge[] {
  const tools = c ? c.tools.Prompt + c.tools.Agent + c.tools.App + c.tools.Other : 0;
  return [
    { id: "idea", label: "First idea", earned: !!c && c.ideas > 0, hint: "Submit an idea to AI Initiatives" },
    { id: "tool", label: "Toolmaker", earned: tools > 0, hint: "Share a prompt, agent or app in the AI Library" },
    { id: "live", label: "Shipped", earned: !!c && c.live > 0, hint: "Have one of your ideas go live" },
    { id: "likes", label: "Crowd favourite", earned: !!c && c.likes >= 10, hint: "Get 10 likes on your tools" },
  ];
}
